// ---------------------------------------------------------------------------
// Callout, ported from the design system (R7)
//
// A bordered row that states a condition, with the number in the title, and
// offers the action that resolves it. One per page. `critical` is announced
// (role alert), the others are a status. `loading` swaps the icon for a
// spinner while what it reports is still under way.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { button } from './button.js';
import { cx, node, present } from './helpers.js';
import { type IconName, icon } from './icons.js';

export type CalloutTone = 'info' | 'positive' | 'caution' | 'critical' | 'agent';

const CALLOUT_ICON: Record<string, IconName> = {
  info: 'info',
  positive: 'check',
  caution: 'alert',
  critical: 'alert',
  agent: 'agent',
};

export interface CalloutProps {
  tone?: CalloutTone;
  title?: unknown;
  /** Replaces the tone's icon (clock on a notice that waits for a time). */
  icon?: IconName;
  /** One or two small buttons. */
  actions?: unknown;
  /** Only for agent and info suggestions. */
  onDismiss?: () => void;
  loading?: boolean;
  className?: string;
}

export function callout(p: CalloutProps, content?: unknown): TemplateResult {
  const tone = p.tone || 'info';
  const mark = p.loading
    ? html`<span class="sdr-spinner sdr-callout__icon" aria-hidden="true"></span>`
    : icon({ name: (p.icon || CALLOUT_ICON[tone]) as IconName, size: 16, className: 'sdr-callout__icon' });
  return html`<div
    class=${cx('sdr-callout', `sdr-callout--${tone}`, p.className)}
    role=${tone === 'critical' ? 'alert' : 'status'}
    aria-busy=${ifDefined(p.loading ? 'true' : undefined)}
  >
    ${mark}
    <div class="sdr-callout__body">
      ${present(p.title) ? html`<div class="sdr-callout__title">${node(p.title)}</div>` : null}
      ${present(content) ? html`<div class="sdr-callout__text">${node(content)}</div>` : null}
    </div>
    ${present(p.actions) ? html`<div class="sdr-callout__actions">${node(p.actions)}</div>` : null}
    ${
      p.onDismiss
        ? button({ variant: 'ghost', size: 'sm', icon: 'close', ariaLabel: 'Dismiss', onClick: () => p.onDismiss?.() })
        : null
    }
  </div>`;
}
